import turfBbox from '@turf/bbox';
import turfBboxPolygon from '@turf/bbox-polygon';
import { BBox, Feature } from '@turf/helpers';
import turfTransformScale from '@turf/transform-scale';
import axios, { CancelTokenSource } from 'axios';
import { DateTime } from 'luxon';
import mapboxgl, { GeoJSONSource, LngLatLike } from 'mapbox-gl';
import { Observable, Subject } from 'rxjs';
import { Component, h, render, VNode } from 'vue';

import Colors from '@/Colors';
import { MapInventoryType } from '@/common/enums/map.enums';
import {
  Filter,
  FilterCollection,
  FilterComparisonOperator,
  FilterLogicalOperator,
  QueryParams,
  Unit,
} from '@/common/models';
import MediaOwner from '@/common/models/media-owner/MediaOwner';
import UnitCluster, { IUnitClusterApi } from '@/common/models/unit/UnitCluster';
import { uniq } from '@/globals';
import MapUnitClusterControl from '@/org-app/components/geofenceGroups/MapUnitClusterControl';
import MediaOwnerOption from '@/org-app/components/geofenceGroups/media-owner-legend/MediaOwnerOption.model';
import MediaOwnersLegend from '@/org-app/components/geofenceGroups/media-owner-legend/MediaOwnersLegend.vue';
import { useMediaOwners } from '@/org-app/services/tanstack/queries';
import { runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import rootStore from '@/store';
import state from '@/store/state';
import formatNumber from '@/utils/NumberFormatter';

const UNIT_CLUSTERS_MAP_ID = 'unit_clusters';
const UNIT_UNCLUSTERED_MAP_ID = 'unit_unclustered';
const UNIT_CLUSTERS_COUNT_ID = 'cluster_count';
const MAP_CONTROL_ID = 'show-unit-control';
const MEDIAOWNERS_CONTROL_ID = 'show-mediaowners-control';

enum MapEvents {
  CLICK = 'click',
  ZOOMEND = 'zoomend',
  DRAGEND = 'dragend',
}

export default class UnitClustersService {
  private readonly map: mapboxgl.Map;

  private unitClusters: UnitCluster[];

  private unitClusters$: Subject<UnitCluster[]>;

  private activeCluster: UnitCluster;

  private units: Unit[];

  private units$: Subject<Unit[]>;

  private getClusteredUnitsFromTargetsCancelTokenSource: CancelTokenSource;

  private getUnitsCancelTokenSource: CancelTokenSource;

  public showUnits: boolean;

  private showUnitControl: MapUnitClusterControl;

  private belowLayerId: string;

  private mediaOwnersLegendComponent: VNode<Component>;

  private shouldFetchMediaOwnersLegendData: boolean;

  private readonly requestedForUtc: DateTime;

  constructor(map: mapboxgl.Map, requestedForUtc: DateTime) {
    this.map = map;
    this.requestedForUtc = requestedForUtc;
  }

  public init(fetchUnitClustersOnInit = false, showLegend = false, belowLayerId?: string): void {
    this.activeCluster = null;
    this.getClusteredUnitsFromTargetsCancelTokenSource = null;
    this.getUnitsCancelTokenSource = null;
    this.unitClusters$ = new Subject<UnitCluster[]>();
    this.units$ = new Subject<Unit[]>();
    this.showUnits = false;
    this.belowLayerId = belowLayerId || null;
    this.shouldFetchMediaOwnersLegendData = false;

    if (this.map) {
      this.initMap(fetchUnitClustersOnInit);

      if (showLegend) {
        this.drawLegend();
      }
    }
  }

  public $destroy(): void {
    this.clearMap();
  }

  public toggleUnitView(): void {
    this.showUnits = !this.showUnits;

    if (this.showUnits) {
      this.showUnitControl.setActiveClasses();
      this.initMap(true);
    } else {
      this.showUnitControl.setInactiveClasses();
      this.clearMap();
    }
  }

  static hasPlanSubscription(): boolean {
    return !!rootStore.state.orgApp?.organizationSettings?.enablePlansFeature;
  }

  /**
   * The unit details has been put behind a paywall
   * To see everything, the user needs to have a plan subscription
   * Otherwise, they will only see the basic info such as unit name
   */
  static unitIncludeParams(): string[] {
    if (UnitClustersService.hasPlanSubscription()) {
      return [];
    }

    return ['unit_id', 'name', 'external_id', 'latitude', 'longitude', 'mediaType', 'mediaTypeID'];
  }

  static unitExpandParams(): string[] {
    if (!UnitClustersService.hasPlanSubscription()) {
      return [];
    }

    return ['site'];
  }

  private onZoomend(): void {
    this.map.on(MapEvents.ZOOMEND, () => {
      this.fetchUnitClusters();
    });
  }

  private onDragend(): void {
    this.map.on(MapEvents.DRAGEND, () => {
      this.fetchUnitClusters();
    });
  }

  private onClick(): void {
    this.map.on(MapEvents.CLICK, [UNIT_CLUSTERS_MAP_ID, UNIT_UNCLUSTERED_MAP_ID], (e) => {
      this.handleClusterClick(e);
    });

    this.map.on(MapEvents.CLICK, () => {
      this.handleMapClick();
    });
  }

  private drawUnitClusters(): void {
    if (!this.map.getSource(UNIT_CLUSTERS_MAP_ID)) {
      this.map.addSource(UNIT_CLUSTERS_MAP_ID, { type: 'geojson', data: this.unitClustersFeatureCollection });
    }

    (this.map.getSource(UNIT_CLUSTERS_MAP_ID) as GeoJSONSource).setData(this.unitClustersFeatureCollection);

    if (this.map.getLayer(UNIT_CLUSTERS_MAP_ID)) {
      this.map.removeLayer(UNIT_CLUSTERS_MAP_ID);
      this.map.removeLayer(UNIT_CLUSTERS_COUNT_ID);
      this.map.removeLayer(UNIT_UNCLUSTERED_MAP_ID);
    }

    this.map.addLayer(
      {
        id: UNIT_CLUSTERS_COUNT_ID,
        type: 'symbol',
        source: UNIT_CLUSTERS_MAP_ID,
        filter: ['>', 'count', 1],
        layout: {
          'text-field': '{countFormatted}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
          'text-size': 14,
        },
        paint: {
          'text-color': '#ffffff',
        },
      },
      this.belowLayerId,
    );

    this.map.addLayer(
      {
        id: UNIT_CLUSTERS_MAP_ID,
        type: 'circle',
        source: UNIT_CLUSTERS_MAP_ID,
        filter: ['>', 'count', 1],
        paint: {
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'isHighlighted'], false],
            Colors.success,
            Colors.actions,
          ],
          'circle-opacity': 0.9,
          'circle-opacity-transition': { duration: 0 },
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': 1,
          'circle-pitch-alignment': 'map',
          'circle-radius': {
            property: 'count',
            type: 'interval',
            stops: [
              [0, 20],
              [100, 25],
              [750, 30],
            ],
          },
        },
      },
      UNIT_CLUSTERS_COUNT_ID,
    );

    this.map.addLayer(
      {
        id: UNIT_UNCLUSTERED_MAP_ID,
        type: 'circle',
        source: UNIT_CLUSTERS_MAP_ID,
        filter: ['==', 'count', 1],
        paint: {
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'isHighlighted'], false],
            Colors.success,
            Colors.actions,
          ],
          'circle-radius': 8,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#fff',
        },
      },
      this.belowLayerId,
    );
  }

  private async fetchUnitClusters(): Promise<void> {
    if (!this.showUnits) return;

    const mapBounds = this.map?.getBounds();
    const bbox: BBox = mapBounds
      ? [
          mapBounds.getSouthWest().lng,
          mapBounds.getSouthWest().lat,
          mapBounds.getNorthEast().lng,
          mapBounds.getNorthEast().lat,
        ]
      : [-180, -90, 180, 90];
    const poly = turfBboxPolygon(bbox);
    const scaledPoly = turfTransformScale(poly, 1.2);
    const scaledBoxPoly = turfBbox(scaledPoly);
    const scaledBbox = [
      Math.max(Math.min(bbox[0], scaledBoxPoly[0]), -180),
      Math.max(Math.min(bbox[1], scaledBoxPoly[1]), -90),
      Math.min(Math.max(bbox[2], scaledBoxPoly[2]), 180),
      Math.min(Math.max(bbox[3], scaledBoxPoly[3]), 90),
    ];

    if (this.getClusteredUnitsFromTargetsCancelTokenSource === null) {
      this.getClusteredUnitsFromTargetsCancelTokenSource = axios.CancelToken.source();
    }

    const params = {
      params: {
        location_ids: [],
        site_ids: [],
        network_ids: [],
        requested_for_utc: this.requestedForUtc,
      },
      minLat: scaledBbox[1],
      maxLat: scaledBbox[3],
      minLng: scaledBbox[0],
      maxLng: scaledBbox[2],
      zoom: Math.round(this.map.getZoom()),
      cancelTokenSource: this.getClusteredUnitsFromTargetsCancelTokenSource,
      type: MapInventoryType.UNITS,
      withUnitIds: false,
      withMediaOwnerInformation: true,
    };

    const action = UnitClustersService.isCurator
      ? 'exchApp/getClusteredUnitsFromTargets'
      : 'orgApp/getClusteredUnitsFromTargets';
    const res = await rootStore.dispatch(action, params);
    this.unitClusters = res.map((x: IUnitClusterApi) => UnitCluster.fromApi(x));
    this.unitClusters$.next(this.unitClusters);
    this.drawUnitClusters();
    this.drawMediaOwnersLegend();
  }

  private async fetchUnits(unitIds: number[], drawOnMap = true): Promise<void | Unit[]> {
    if (this.getUnitsCancelTokenSource === null) {
      this.getUnitsCancelTokenSource = axios.CancelToken.source();
    }

    const queryParams = QueryParams.for({
      top: null,
      include: UnitClustersService.unitIncludeParams(),
      expand: UnitClustersService.unitExpandParams(),
      filterCollection: new FilterCollection(
        [
          new Filter('site.active', [true], FilterComparisonOperator.EQUAL),
          new Filter('active', [true], FilterComparisonOperator.EQUAL),
          new Filter('unit_id', unitIds, FilterComparisonOperator.IN),
        ],
        FilterLogicalOperator.AND,
      ),
    });
    const params = {
      queryParams,
      cancelTokenSource: this.getUnitsCancelTokenSource,
      showLoader: true,
    };
    const res = (await rootStore.dispatch('orgApp/getUnits', params))?.items;

    if (drawOnMap) {
      this.units = res;
      this.units$.next(this.units);
    }
    return res;
  }

  private async fetchMediaOwnersLegendData(): Promise<MediaOwner[]> {
    const mediaOwnersIds = uniq(
      this.unitClusters
        .map((uc) => uc.ownerInfo)
        .map((x) => Object.keys(x).map((k) => parseInt(k, 10)))
        .flat(),
    );
    const allMediaOwners = await UnitClustersService.fetchMediaOwners();
    return allMediaOwners.filter((mo) => mediaOwnersIds.includes(mo.id));
  }

  private static fetchMediaOwners(): Promise<MediaOwner[]> {
    return runQueryInScope(useMediaOwners);
  }

  private handleClusterClick(
    e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined } & mapboxgl.EventData,
  ): void {
    if (!this.showUnits) return;
    const [feature] = e.features;
    const { id, expansionZoom } = feature.properties;
    const center: LngLatLike = [feature.properties.lng, feature.properties.lat];
    const duration = 500;

    this.activeCluster = this.unitClusters.find((x) => x.id === id);

    const unitIds = this.activeCluster?.unitIds;
    // If we got unit ids, fetch the units, otherwise zoom in on the cluster to open it up
    if (unitIds?.length) {
      this.map.easeTo({ center, duration });
      this.fetchUnits(unitIds);
    } else {
      this.map.easeTo({ center, zoom: expansionZoom, duration });
    }
  }

  // clicking on the map should reset the active cluster and its active units
  private handleMapClick(): void {
    this.activeCluster = null;
    this.units$.next([]);
  }

  public get activeUnits$(): Observable<Unit[]> {
    return this.units$.asObservable();
  }

  public get activeUnitClusters$(): Observable<UnitCluster[]> {
    return this.unitClusters$.asObservable();
  }

  private get unitClustersFeatureCollection(): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
    const features: Feature<GeoJSON.Geometry>[] =
      this.unitClusters?.map((x) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [x.longitude, x.latitude],
        },
        id: x.id, // This is used by mapbox setFeatureState()
        properties: {
          id: x.id, // This is used by GeofenceListAndMap.activateGeofence()
          bounds: x.bounds,
          expansionZoom: x.expansionZoom,
          count: x.count,
          countFormatted: x.count >= 1000 ? formatNumber(x.count, '0.0a') : `${x.count}`,
          lat: x.latitude,
          lng: x.longitude,
          latLngString: `${x.latitude},${x.longitude}`,
        },
      })) || [];

    return { type: 'FeatureCollection', features };
  }

  private drawLegend(): void {
    this.showUnitControl = new MapUnitClusterControl();
    this.showUnitControl.clickHandler = () => this.toggleUnitView();
    this.showUnitControl.id = MAP_CONTROL_ID;

    this.map.addControl(this.showUnitControl, 'top-right');

    if (this.showUnits) {
      this.showUnitControl.setActiveClasses();
    } else {
      this.showUnitControl.setInactiveClasses();
    }
  }

  private async reloadMediaOwnersLegend(): Promise<void> {
    // fetch MO data if we don't have it yet, to make sure mediaOwnersInfo getter is populated
    await this.fetchMediaOwnersLegendData();
    const mediaOwnerOptions: MediaOwnerOption[] = [];

    this.unitClusters.forEach((uc) => {
      const { mediaOwnersInfo } = uc;

      mediaOwnersInfo.forEach((mediaOwnerInfo) => {
        const isAlreadyAdded = mediaOwnerOptions.find((mo) => mo.id === mediaOwnerInfo.id);

        if (isAlreadyAdded) {
          isAlreadyAdded.unitCount += mediaOwnerInfo.unitCount;
        } else {
          mediaOwnerOptions.push(mediaOwnerInfo);
        }
      });
    });

    this.mediaOwnersLegendComponent.component.props.mediaOwnerOptions = mediaOwnerOptions;
  }

  private async drawMediaOwnersLegend(): Promise<void> {
    // if user org is supplier, no need to show
    if (!UnitClustersService.isBuyer) return;

    // the legend is already there, don't draw it again, just update the props
    if (this.mediaOwnersLegendComponent && this.shouldFetchMediaOwnersLegendData) {
      await this.reloadMediaOwnersLegend();
      return;
    }

    const canvas = this.map.getContainer();
    const canvasParent = canvas.parentNode;

    if (canvasParent.querySelector(`#${MEDIAOWNERS_CONTROL_ID}`)) {
      canvasParent.removeChild(canvasParent.querySelector(`#${MEDIAOWNERS_CONTROL_ID}`));
    }

    const container = document.createElement('div');
    container.id = MEDIAOWNERS_CONTROL_ID;
    container.style.position = 'absolute';
    container.style.left = '1rem';
    container.style.top = '1rem';
    container.style.flexGrow = '1';
    container.style.margin = '0';
    container.style.maxHeight = '95%';
    container.style.width = '25%';
    container.style.overflow = 'auto';

    this.mediaOwnersLegendComponent = h(MediaOwnersLegend, {
      mediaOwnerOptions: [],
      isOpened: (isOpen: boolean) => {
        this.shouldFetchMediaOwnersLegendData = isOpen;

        if (this.shouldFetchMediaOwnersLegendData) {
          this.reloadMediaOwnersLegend();
        }
      },
      onMediaOwnerSelected: (mo: MediaOwner) => this.onMediaOwnerSelected(mo),
    });
    render(this.mediaOwnersLegendComponent, container);

    canvasParent.appendChild(container);
  }

  /**
   * When a media owner is selected, we need to highlight the clusters that have units from that media owner
   */
  private onMediaOwnerSelected(mo: MediaOwner): void {
    const moClusters = this.unitClusters.filter((uc) => !!uc.ownerInfo[mo?.id.toString()]);

    this.unitClusters.forEach((uc) => {
      this.map.setFeatureState(
        { source: UNIT_CLUSTERS_MAP_ID, id: uc.id },
        { isHighlighted: !!moClusters.find((u) => uc.id === u.id) },
      );
    });
  }

  private initMap(fetchUnitClustersOnInit: boolean): void {
    this.onZoomend();
    this.onDragend();
    this.onClick();

    if (fetchUnitClustersOnInit) {
      this.fetchUnitClusters();
    }
  }

  private clearMap(): void {
    this.map.off(MapEvents.CLICK, this.handleMapClick);
    this.map.off(MapEvents.ZOOMEND, this.fetchUnitClusters);
    this.map.off(MapEvents.DRAGEND, this.fetchUnitClusters);

    const layerIds = [UNIT_CLUSTERS_COUNT_ID, UNIT_UNCLUSTERED_MAP_ID, UNIT_CLUSTERS_MAP_ID];

    layerIds.forEach((id) => {
      if (this.map.getLayer(id)) {
        this.map.removeLayer(id);
      }

      if (this.map.getSource(id)) {
        this.map.removeSource(id);
      }
    });

    this.unitClusters = [];

    this.units$?.next([]);
  }

  private static get isBuyer(): boolean {
    const organization = state?.orgApp?.organization;
    return organization?.isDemandOrganization || false;
  }

  private static get isCurator(): boolean {
    const organization = state?.orgApp?.organization;
    return organization?.isCuratorOrganization || false;
  }
}
